import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  KanbanComponent,
  ColumnsDirective,
  ColumnDirective,
} from "@syncfusion/ej2-react-kanban";
import { Button } from "primereact/button";
import { Accordion, AccordionTab } from "primereact/accordion";
import { LuGoal, LuCheck } from "react-icons/lu";
import { TbProgressCheck } from "react-icons/tb";
import { MdOutlineAddTask, MdOutlineBugReport } from "react-icons/md";
import { IoWarningOutline } from "react-icons/io5";

import { NodeData, KanbanCardData, Task, CustomProperty } from "../types/types";
import ReactFlow from "@xyflow/react";
import type { Node } from "@xyflow/react";
import KanbanCard from "./KanbanCard";
import { useStore } from "../App/store";
import { getCalculatedValue } from "../Helpers/Calculations";
import { stripHtmlFromLabel } from "../utils/textFormatting";
//import successSound from "../assets/sounds/congrats.mp3";

interface KanbanAreaProps {
  nodes: Node<NodeData>[];
  handleAddUpdateTask: (
    id: string | number,
    title: string,
    nodeTitle: string,
    status: string,
    summary: string,
    priority?: string,
    tags?: string,
    assignee?: string,
    estimate?: number,
    rankId?: number,
    color?: string,
    className?: string,
    startDate?: Date | string,
    endDate?: Date | string,
  ) => void;
  handleDeleteTask: (taskId: string) => void;
}

const HeaderTemplate = (props) => (
  <div
    className="custom-header"
    style={{ textAlign: "left", fontSize: "16px" }}
  >
    <span className="header-icon">
      {props.headerText === "To Do" ? (
        <MdOutlineAddTask />
      ) : props.headerText === "Doing" ? (
        <TbProgressCheck />
      ) : props.headerText === "Testing" ? (
        <MdOutlineBugReport />
      ) : props.headerText === "Impediment" ? (
        <IoWarningOutline />
      ) : props.headerText === "Done" ? (
        <LuCheck />
      ) : (
        "🟢"
      )}
    </span>
    <span className="header-text"> {props.headerText}</span>
  </div>
);

const KanbanArea: React.FC<KanbanAreaProps> = ({
  nodes,
  handleAddUpdateTask,
  handleDeleteTask,
}) => {
  // Get nodes directly from store as the single source of truth
  const storeNodes = useStore((state) => state.nodes);
  const currency = useStore((state) => state.currency);
  const [task, setTask] = useState<Task | null>(null);
  const [showTaskCard, setShowTaskCard] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Ref to Kanban instance
  const kanbanRef = useRef<KanbanComponent | null>(null);

  // Helper function to build path from node to root
  const buildNodePath = (nodeId: string): string => {
    const path: string[] = [];
    let currentNodeId: string | undefined = nodeId;

    while (currentNodeId) {
      const currentNode = storeNodes.find((n) => n.id === currentNodeId);
      if (!currentNode) break;

      // Skip root and center nodes
      if (currentNode.type !== "center" && currentNode.id !== "root") {
        path.unshift(stripHtmlFromLabel(currentNode.data.label || ""));
      }

      currentNodeId = currentNode.parentId;
    }

    return path.join(" / ");
  };

  // Compute kanban items directly from store nodes using useMemo
  const kanbanCardItems = useMemo(() => {
    const filteredNodes = storeNodes.filter(
      (node) => node.type !== "center" && node.data.task?.id,
    );

    // Build per-status rank counters so each column has its own priority sequence
    const statusCounters: Record<string, number> = {};

    const boardData: KanbanCardData[] = filteredNodes.map((node) => {
      const normalizedStatus =
        node.data.task?.status === undefined ||
        node.data.task?.status === "none"
          ? "Open"
          : node.data.task?.status;

      statusCounters[normalizedStatus] =
        (statusCounters[normalizedStatus] || 0) + 1;

      // Build full path from node's parent to root (excluding the node itself and root)
      const parentPath = node.parentId ? buildNodePath(node.parentId) : "";

      return {
        id: node.id,
        title: node.data.label,
        parentLabel: parentPath,
        status:
          normalizedStatus === "InProgress" ||
          normalizedStatus === "Testing" ||
          normalizedStatus === "Impediment" ||
          normalizedStatus === "Done" ||
          normalizedStatus === "Open"
            ? normalizedStatus
            : "Open",
        summary: node.data.task?.summary
          ? node.data.task?.summary.substring(0, 250) + "..."
          : "",
        priority: node.data.task?.priority || "",
        tags: node.data.task?.tags || "",
        estimate: node.data.task?.estimate || 0,
        assignee: node.data.task?.assignee || "",
        // Use existing rank if present; fallback to new sequential counter
        rankId:
          node.data.task?.rankId && typeof node.data.task?.rankId === "number"
            ? node.data.task?.rankId
            : statusCounters[normalizedStatus],
        startDate: node.data.task?.startDate
          ? new Date(node.data.task.startDate)
          : undefined,
        endDate: node.data.task?.endDate
          ? new Date(node.data.task.endDate)
          : undefined,
        customProperties: node.data.customProperties || [],
      };
    });

    return boardData;
  }, [storeNodes]);

  const addUpdateTask = (
    id: string | number,
    title: string,
    nodeTitle: string,
    status: string,
    summary: string,
    priority?: string,
    tags?: string,
    assignee?: string,
    estimate?: number,
    rankId?: number,
    color?: string,
    className?: string,
    startDate?: Date,
    endDate?: Date,
  ) => {
    const taskObj: Task = {
      id:
        typeof id === "string" || typeof id === "number"
          ? id
          : `user-${Date.now()}`,
      title,
      nodeTitle,
      status,
      summary,
      priority,
      tags,
      assignee,
      estimate,
      rankId,
      color,
      className,
      startDate,
      endDate,
    };

    handleAddUpdateTask(
      taskObj.id,
      taskObj.title,
      taskObj.nodeTitle,
      taskObj.status,
      taskObj.summary,
      taskObj.priority,
      taskObj.tags,
      taskObj.assignee,
      taskObj.estimate,
      taskObj.rankId,
      taskObj.color,
      taskObj.className,
      taskObj.startDate,
      taskObj.endDate,
    );
    setTask(null);
    setShowTaskCard(false);
  };

  const deleteTask = (id: string) => {
    handleDeleteTask(id);
    setTask(null);
    setShowTaskCard(false);
  };

  // Handler for Kanban drag start
  const onKanbanDragStart = (args: any) => {
    setIsDragging(true);
  };

  // Handler for Kanban drag stop
  const onKanbanDragStop = (args: any) => {
    // Will be cleared by actionComplete
  };

  // Handler for Kanban events like card move / reorder
  const onKanbanActionComplete = async (args: any) => {
    if (args.requestType === "cardChanged") {
      // Persist card field changes (including status if moved across columns)
      if (args.changedRecords?.length > 0) {
        // Wait for all updates to complete before clearing drag flag
        await Promise.all(
          args.changedRecords.map((card: any) =>
            addUpdateTask(
              card.Id,
              card.Title,
              card.nodeTitle || card.Title,
              card.Status,
              card.Summary || "",
              card.Priority,
              card.Tags,
              card.Assignee,
              card.Estimate,
              card.RankId,
              card.Color,
              card.ClassName,
              card.StartDate,
              card.EndDate,
            ),
          ),
        );

        // Give React time to propagate the store update
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      // Clear drag flag after updates are complete
      setIsDragging(false);
    }
  };
  return (
    <div className="kanban-container">
      {kanbanCardItems.length === 0 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            pointerEvents: "none",
            background: "transparent",
          }}
        >
          <h1 style={{ textAlign: "center", color: " rgb(18, 102, 86)" }}>
            Mark items in your map view as tasks to act on them here.
          </h1>
        </div>
      )}
      <KanbanComponent
        ref={kanbanRef}
        id="kanban"
        keyField="Status"
        // Sort within columns by RankId to reflect priority order
        sortSettings={{ field: "RankId", direction: "Ascending" }}
        dataSource={kanbanCardItems.map((item) => ({
          ...item,
          Id: item.id,
          Title: item.title,
          ParentLabel: item.parentLabel,
          nodeTitle: item.title,
          Status: item.status,
          Summary: item.summary,
          Priority: item.priority,
          Tags: item.tags,
          Assignee: item.assignee,
          Estimate: item.estimate,
          RankId: item.rankId,
          Color: item.color,
          ClassName: item.className,
          StartDate: item.startDate,
          EndDate: item.endDate,
          CustomProperties: item.customProperties,
        }))}
        cardSettings={{
          contentField: "Summary",
          headerField: "Id",
          template: (props: any) => {
            // Filter custom properties that have values
            const propertiesWithValues = (props.CustomProperties || []).filter(
              (prop: CustomProperty) =>
                prop.value !== undefined &&
                prop.value !== null &&
                prop.value !== "" &&
                prop.visible !== false,
            );

            return (
              <div
                className="custom-kanban-card"
                style={{
                  textAlign: "left",
                  paddingLeft: "12px",
                  borderLeft: "4px solid   rgb(18, 102, 86)",
                  background: "#fff",
                  borderRadius: "4px",
                  margin: "0px 0",
                  minHeight: "100px",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  // Prevent card click when clicking accordion
                  if (
                    !(e.target as HTMLElement).closest(".p-accordion") &&
                    !(e.target as HTMLElement).closest(".p-accordion-header")
                  ) {
                    setTask(props);
                    setShowTaskCard(true);
                  }
                }}
              >
                <div
                  className="card-content"
                  style={{ textAlign: "left", minHeight: "80px" }}
                >
                  <div className="card-title">
                    {stripHtmlFromLabel(props.Title || "")}
                  </div>
                  <div className="card-summary">
                    Summary: {props.Summary || ""}
                  </div>
                  <div className="card-date">
                    Start Date:{" "}
                    {props.StartDate
                      ? props.StartDate?.toLocaleDateString()
                      : ""}
                  </div>
                  <div className="card-date">
                    Due Date:{" "}
                    {props.EndDate ? props.EndDate.toLocaleDateString() : ""}
                  </div>
                  <div className="card-estimate">
                    Estimate: {props.Estimate || ""}
                  </div>
                  <div className="card-avatar">
                    <span className="avatar-text">
                      Owner: {props.Assignee || ""}
                    </span>
                  </div>

                  {/* Custom Properties Accordion */}
                  {propertiesWithValues.length > 0 && (
                    <div
                      style={{ marginTop: "12px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Accordion>
                        <AccordionTab
                          header={`Properties (${propertiesWithValues.length})`}
                          pt={{
                            headerAction: {
                              style: {
                                padding: "8px 12px",
                                fontSize: "13px",
                                background: "#f8f9fa",
                              },
                            },
                            content: {
                              style: {
                                padding: "8px 12px",
                                fontSize: "13px",
                              },
                            },
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px",
                            }}
                          >
                            {propertiesWithValues.map(
                              (prop: CustomProperty, index: number) => {
                                let displayValue = String(prop.value);

                                if (prop.type === "date" && prop.value) {
                                  displayValue = new Date(
                                    prop.value,
                                  ).toLocaleDateString();
                                } else if (prop.type === "boolean") {
                                  displayValue = prop.value ? "Yes" : "No";
                                } else if (prop.type === "currency") {
                                  displayValue = `${prop.value} ${
                                    currency || "EUR"
                                  }`;
                                } else if (prop.type === "calculated") {
                                  const calculatedResult = getCalculatedValue(
                                    prop,
                                    props.CustomProperties || [],
                                    currency || "EUR",
                                  );
                                  displayValue = calculatedResult?.value || "0";
                                }

                                return (
                                  <div
                                    key={index}
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      padding: "0px 0",
                                      borderBottom:
                                        index < propertiesWithValues.length - 1
                                          ? "1px solid #e9ecef"
                                          : "none",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color: "#495057",
                                      }}
                                    >
                                      {prop.name}:
                                    </span>
                                    <span style={{ color: "#6c757d" }}>
                                      {displayValue}
                                    </span>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        </AccordionTab>
                      </Accordion>
                    </div>
                  )}
                </div>
                <div className="card-footer" style={{ textAlign: "left" }}>
                  {props.ParentLabel && (
                    <div className="card-path">
                      <span style={{ fontWeight: "normal" }}>Path:</span>{" "}
                      {props.ParentLabel}
                    </div>
                  )}
                </div>
              </div>
            );
          },
        }}
        dragStart={onKanbanDragStart}
        dragStop={onKanbanDragStop}
        actionComplete={onKanbanActionComplete}
      >
        <ColumnsDirective>
          <ColumnDirective
            headerText="To Do"
            keyField="Open"
            template={HeaderTemplate}
          />
          <ColumnDirective
            headerText="Doing"
            keyField="InProgress"
            template={HeaderTemplate}
          />
          <ColumnDirective
            headerText="Testing"
            keyField="Testing"
            template={HeaderTemplate}
          />
          <ColumnDirective
            headerText="Impediment"
            keyField="Impediment"
            template={HeaderTemplate}
          />
          <ColumnDirective
            headerText="Done"
            keyField="Done"
            template={HeaderTemplate}
          />
        </ColumnsDirective>
      </KanbanComponent>

      <KanbanCard
        visible={showTaskCard}
        nodeTask={task}
        setShowKanbanCard={setShowTaskCard}
        handleAddUpdateTask={addUpdateTask}
        handleDeleteTask={deleteTask}
      />
    </div>
  );
};

export default KanbanArea;
